import * as React from 'react'
import {
  UWPNotifier,
  OutputAction,
  DownloadStatus,
  StatusMessage,
  AllDownloadsPayload,
  DownloadStatusPayload,
  BeforePlayModals,
} from '../UWPNotifier'
import { BeforePlayModal, shouldOpenBeforePlayModal } from './BeforePlayModal'
import { triggerPlayback } from '../triggers'
import { DownloadButtonWithLabel, DownloadButton } from './DownloadButton'
import { DownloadStatusMessages } from './DownloadStatusMessages'
import { count } from '../../../widgets/DVWebNodeSPA/utils/csm'
import { isShowPlayButton, isShowModalBeforePlay } from '../../../widgets/DVWebNodeSPA/utils/downloads'
import { Modal } from '@amzn/dvui/dist/Modal'
import { withSpaContext, SpaContextProps } from '../SpaContext'
import { fillValues, buildDvString, ClientsideLogger, safeBool } from '@utils'
import * as st from './DownloadButton.module.scss'
import history from '../../../widgets/DVWebNodeSPA/history'
import cn from 'classnames'
import { RestrictionModal, callPassRestrictions } from '../restrictions/Restrictions'
import * as Types from 'DVWebNodeWpsRuntime'
import { OnPinSubmit, OnPinSubmitArgs } from '../restrictions/PinModalContent'
import { withRouter, RouteComponentProps } from 'react-router-dom'
import { AtfTreatmentProps } from '../../../widgets/DVWebNodeDetail/reducers/features'
import * as equal from 'fast-deep-equal'

const logger = new ClientsideLogger('DVWebNodeSPA/DownloadActions')
const refTagBase = 'pvwa_dl'
const refTagRegEx = /^[\w]+$/

export type DownloadActionProps = {
  downloadInfo?: {
    downloadURL: string
    titleID: string
    episodeNumber: undefined | number
    isMovie: boolean
  }
  isAtf?: boolean
  isErrored?: boolean
}

export type Params = {[key: string]: string}
export type DownloadActionWithSpaContextProps = RouteComponentProps<Params> & SpaContextProps & DownloadActionProps & AtfTreatmentProps

type DownloadActionState = {
  downloadStatus: DownloadStatus
  downloadProgress: number
  downloadStatusMessages: StatusMessage[]
  downloadId?: string
  downloadActionOpen: boolean
  showModalBeforePlay: BeforePlayModals
  downloadStatusPayload: DownloadStatusPayload
  showRestrictionModal: boolean
  restrictionModalProps: Types.atv.wps.RestrictionModalProps
}

const getDownloadStatus = (titleID: string, downloads: AllDownloadsPayload) => {
  return titleID && downloads && downloads[titleID]
}

const getDownloadId = (props: DownloadActionWithSpaContextProps) => {
  return (
    props.downloads &&
    props.downloadInfo &&
    props.downloads[props.downloadInfo.titleID] &&
    props.downloads[props.downloadInfo.titleID].DownloadId
  )
}

const getDownloadStateFromProps = (props: DownloadActionWithSpaContextProps, state?: DownloadActionState) => {
  const downloadStatus = props.downloadInfo && getDownloadStatus(props.downloadInfo.titleID, props.downloads)

  return {
    downloadStatus: (downloadStatus && DownloadStatus[downloadStatus.Status]) || DownloadStatus.IDLE,
    downloadProgress: (downloadStatus && downloadStatus.Progress) || 0,
    downloadStatusMessages: downloadStatus && downloadStatus.Messages,
    downloadId: downloadStatus && props.downloads[props.downloadInfo.titleID].DownloadId,
    downloadActionOpen: state ? state.downloadActionOpen : false,
    downloadStatusPayload: downloadStatus,
    showModalBeforePlay: downloadStatus && isShowModalBeforePlay(downloadStatus),
    showRestrictionModal: state ? state.showRestrictionModal : false,
    restrictionModalProps: state && state.restrictionModalProps,
  }
}

type GetRefTag = {source: string, page: string, section: string}
const getRefTag = ({source, page, section}: GetRefTag) => {
  return [refTagBase, source, page, section].join('_')
}


export class DownloadActionWithSpaContext extends React.Component<
  DownloadActionWithSpaContextProps,
  DownloadActionState
> {
  errorModal = React.createRef<Modal>()
  refTag: string = `${refTagBase}_unknown`
  beforePlayModal = React.createRef<Modal>()
  
  constructor(props: DownloadActionWithSpaContextProps) {
    super(props)
    this.state = getDownloadStateFromProps(props)
  }
  
  shouldComponentUpdate = (nextProps: DownloadActionWithSpaContextProps, nextState: DownloadActionState) => {
    if(
      this.props.downloadInfo && nextProps.downloadInfo && equal(getDownloadStatus(this.props.downloadInfo.titleID, this.props.downloads), getDownloadStatus(nextProps.downloadInfo.titleID, nextProps.downloads)) &&
      this.props.isErrored === nextProps.isErrored &&
      this.state === nextState) {
      return false
    }
    
    return true
  }
  
  changeDownloadStatus = (downloadStatus: DownloadStatus) => {
    this.setState({
      downloadStatus,
      downloadId: getDownloadId(this.props),
    })
  }

  passRestrictionsBeforeDownload = async ({input, submitEndpoint}: OnPinSubmitArgs = {} as OnPinSubmitArgs) => {
    const {
      downloadInfo: { titleID },
      saveOfflineData,
      requestContext,
    } = this.props

    const restrictionInput = {
      ...(input && input),
      contentId: titleID,
    }
    const restrictions = await callPassRestrictions(restrictionInput, requestContext, submitEndpoint)

    logger.debug('callPassRestrictions result', restrictions)

    if (restrictions) {
      if(safeBool(restrictions.canContinue)) {
        this.closeRestrictionModal()
        this.changeDownloadStatus(DownloadStatus.WAITING)
        saveOfflineData(titleID)
        this.triggerDownload(titleID)
      } else {
        this.setState({
          showRestrictionModal: true,
          restrictionModalProps: restrictions.modal,
        })
      }
      logger.count('DVWeb::PassRestrictions::CallFailed', 0, true)
    } else {
      logger.cxfatal('PassRestrictions call failed')
      logger.count('DVWeb::PassRestrictions::CallFailed', 1, true)
      this.showFallbackErrorModal()
    }
  }

  onPinSubmit: OnPinSubmit = (args) => {
    this.passRestrictionsBeforeDownload(args)
  }

  triggerDownload = (titleId: string) => {
    UWPNotifier.getInstance().publish(OutputAction.Download_Start, {
      titleId,
      refTag: this.refTag,
    })
  }

  showFallbackErrorModal = () => {
    const {strings} = this.props

    const errorModal = {
      title: strings.DV_WEB_download_pin_error_title,
      content: {
        text: buildDvString(strings.DV_WEB_download_restriction_error_description)
      } as Types.atv.wps.TextModalContentProps,
      buttons: []
    } as Types.atv.wps.RestrictionModalProps

    this.setState({
      showRestrictionModal: true,
      restrictionModalProps: errorModal,
    })
  }

  startDownloadProcess = () => {
    this.changeDownloadStatus(DownloadStatus.WAITING)
    this.passRestrictionsBeforeDownload()
  }

  startDownload = (e: React.MouseEvent) => {
    e.preventDefault()
    this.refTag = getRefTag({
      source: 'spa',
      page: 'dp',
      section: this.props.isAtf ? 'atf' : 'btf',
    })
    this.startDownloadProcess()
  }

  pauseDownload = (e: React.MouseEvent) => {
    e.preventDefault()
    UWPNotifier.getInstance().publish(OutputAction.Download_Pause, {
      titleId: this.props.downloadInfo.titleID,
      ...(this.state.downloadId && { downloadId: this.state.downloadId }),
    })
    this.onClick()
  }

  resumeDownload = (e: React.MouseEvent) => {
    e.preventDefault()
    UWPNotifier.getInstance().publish(OutputAction.Download_Resume, {
      titleId: this.props.downloadInfo.titleID,
      ...(this.state.downloadId && { downloadId: this.state.downloadId }),
    })
    this.onClick()
  }

  deleteDownload = (e: React.MouseEvent) => {
    e.preventDefault()
    UWPNotifier.getInstance().publish(OutputAction.Download_Delete, {
      titleId: this.props.downloadInfo.titleID,
      ...(this.state.downloadId && { downloadId: this.state.downloadId }),
    })
    this.onClick()
  }

  cancelDownload = (e: React.MouseEvent) => {
    e.preventDefault()
    UWPNotifier.getInstance().publish(OutputAction.Download_Cancel, {
      titleId: this.props.downloadInfo.titleID,
      ...(this.state.downloadId && { downloadId: this.state.downloadId }),
    })
    this.onClick()
  }

  retryDownload = (e: React.MouseEvent) => {
    e.preventDefault()
    UWPNotifier.getInstance().publish(OutputAction.Download_Retry, {
      titleId: this.props.downloadInfo.titleID,
      ...(this.state.downloadId && { downloadId: this.state.downloadId }),
    })
  }

  playDownload = (e: React.MouseEvent) => {
    e.preventDefault()

    if(this.state.showModalBeforePlay && this.beforePlayModal && shouldOpenBeforePlayModal(this.state.showModalBeforePlay.Type)) {
      this.beforePlayModal.current.open()
    } else {
      triggerPlayback(this.props.downloadInfo.titleID, this.state.downloadId)
    }
    this.onClick()
  }

  viewDetail = (e: React.MouseEvent) => {
    e.preventDefault()
    if (this.props.downloadInfo && this.props.downloadInfo.titleID) {
      history.push(`/detail/${this.props.downloadInfo.titleID}`)
      this.onClick()
    }
  }

  viewError = (e: React.MouseEvent) => {
    e.preventDefault()
    this.errorModal.current.open()
    this.onClick()
  }

  noActionDownload = (e: React.MouseEvent) => {
    e.preventDefault()
    count('no-action')
  }

  getDownloadAction = (showDownloadErrorModal: boolean) => {
    const { strings, isErrored } = this.props
    const isOnDownloadsPage = history && history.location.pathname.indexOf('/downloads') > -1
    // To be done at a later stage: const startDownloadLink = { text: strings.DV_WINAPP_DROPLIST_STARTDL, onClick: this.startDownload, className: st.startDownload }
    const cancelDownloadLink = {
      text: strings.DV_WINAPP_DROPLIST_CANCELDL,
      onClick: this.cancelDownload,
      className: st.cancelDownload,
    }
    const pauseDownloadLink = {
      text: strings.DV_WINAPP_DROPLIST_PAUSEDL,
      onClick: this.pauseDownload,
      className: st.pauseDownload,
    }
    const viewDetailsLink = {
      text: strings.DV_WINAPP_DROPLIST_VIEWDETAIL,
      onClick: this.viewDetail,
      className: st.viewDetails,
    }
    const resumeDownloadLink = {
      text: strings.DV_WINAPP_DROPLIST_RESUMEDL,
      onClick: this.resumeDownload,
      className: st.resumeDownload,
    }
    const retryDownloadLink = {
      text: strings.DV_WINAPP_DROPLIST_RETRYDL,
      onClick: this.retryDownload,
      className: st.retryDownload,
    }
    const deleteDownloadLink = {
      text: strings.DV_WINAPP_DROPLIST_DELETEDL,
      onClick: this.deleteDownload,
      className: st.retryDownload,
    }
    const playDownloadLink = {
      text: strings.DV_WINAPP_DROPLIST_PLAY,
      onClick: this.playDownload,
      className: st.playDownload,
    }
    const viewErrorLink = { text: strings.DV_WINAPP_DROPLIST_ERROR, onClick: this.viewError, className: st.viewError }

    const showPlayButton =
      this.state.downloadStatus === DownloadStatus.COMPLETED
        ? isShowPlayButton(this.state.downloadStatusPayload)
        : false

        
    const condDetailsLink = isOnDownloadsPage ? [viewDetailsLink] : []
    const condPlayButton = showPlayButton ? [playDownloadLink] : []
    const condViewErrorLink = showDownloadErrorModal ? [viewErrorLink] : []
    const disabledOrErrored = { action: this.deleteDownload, links: [deleteDownloadLink, ...condDetailsLink, ...condViewErrorLink] }
    switch (this.state.downloadStatus) {
      case DownloadStatus.IDLE:
      case DownloadStatus.CANCELLED:
      case DownloadStatus.REMOVED:
        return { action: this.startDownload, links: [] }
      case DownloadStatus.QUEUED:
        return { action: this.noActionDownload, links: [cancelDownloadLink, ...condDetailsLink] }
      case DownloadStatus.DOWNLOADING:
        return { action: this.pauseDownload, links: [pauseDownloadLink, cancelDownloadLink, ...condDetailsLink] }
      case DownloadStatus.PAUSED:
        return { action: this.resumeDownload, links: [resumeDownloadLink, cancelDownloadLink, ...condDetailsLink] }
      case DownloadStatus.ERROR:
        return {
          action: this.retryDownload,
          links: [retryDownloadLink, deleteDownloadLink, ...condDetailsLink, ...condViewErrorLink],
        }
      case DownloadStatus.COMPLETED:
        return isErrored ? disabledOrErrored : { action: this.deleteDownload, links: [...condPlayButton, deleteDownloadLink, ...condDetailsLink] }
      case DownloadStatus.DISABLED:
        return disabledOrErrored
      default:
        return { action: this.noActionDownload, links: [deleteDownloadLink] }
    }
  }

  componentDidMount() {
    this.handleDeeplinking()
  }

  handleDeeplinking = () => {
    const { match, location, downloadInfo, isAtf } = this.props
    const { titleId: titleID } = match.params
    const params = new URLSearchParams(location.search)
    const isDeeplink = params.get('action') === 'download' 
    const refTag = params.get('ref_') 
    const isSelectedTitle =  downloadInfo && titleID === downloadInfo.titleID
    const isMovie = downloadInfo && downloadInfo.isMovie
    const isMovieAtf = isAtf && isMovie  
    const isEpisodeBtf = !isAtf && !isMovie // to avoid triggering download twice on seasons
    const isDeeplinkingDownload = isDeeplink && isSelectedTitle && (isMovieAtf || isEpisodeBtf)

    if (isDeeplinkingDownload) {
      if (refTag && refTag.match(refTagRegEx)) {
        this.refTag = refTag
      }
      this.startDownloadProcess()
    }
  }
  
  componentDidUpdate(prevProps: DownloadActionWithSpaContextProps) {
    const prevDownloadState = getDownloadStatus(prevProps.downloadInfo.titleID, prevProps.downloads)
    const prevStatus = prevDownloadState && prevDownloadState.Status
    const prevProgress = prevDownloadState && prevDownloadState.Progress

    const newDownloadState = getDownloadStatus(this.props.downloadInfo.titleID, this.props.downloads)
    const newStatus = newDownloadState && newDownloadState.Status
    const newProgress = newDownloadState && newDownloadState.Progress

    if (prevStatus !== newStatus || prevProgress !== newProgress) {
      this.setState((state, props) => {
        const downloadState = getDownloadStateFromProps(props, state)
        return {
          ...downloadState,
        }
      })
    }
  }

  onClick = (e?: any) => {
    if (e) {
      e.preventDefault()
    }

    this.setState((state) => ({downloadActionOpen: !state.downloadActionOpen}))
  }

  shouldDropListHaveLabel = () => {
    switch (this.state.downloadStatus) {
      case DownloadStatus.QUEUED:
      case DownloadStatus.DOWNLOADING:
      case DownloadStatus.PAUSED:
      case DownloadStatus.ERROR:
        return true
      case DownloadStatus.IDLE:
      case DownloadStatus.WAITING:
      case DownloadStatus.COMPLETED:
        return false
      default:
        return false
    }
  }

  closeRestrictionModal = () => {
    this.changeDownloadStatus(DownloadStatus.IDLE)
    this.setState({ showRestrictionModal: false })
  }

  render() {
    const { downloadInfo, isAtf, strings, isErrored, atfTreatment } = this.props

    const { showModalBeforePlay, downloadStatusMessages, showRestrictionModal, restrictionModalProps } = this.state

    if (!downloadInfo) {
      return null
    }

    const isEpisode = typeof downloadInfo.episodeNumber !== 'undefined'
    const episodeText =
      downloadInfo.episodeNumber === 0
        ? strings.DV_WINAPP_EPISODE_BONUS
        : fillValues(strings.DV_WINAPP_EPISODE_NB, { episodeNumber: String(downloadInfo.episodeNumber) })

    const showDownloadErrorModal = Boolean(downloadStatusMessages && downloadStatusMessages.length)
    const downloadAction = this.getDownloadAction(showDownloadErrorModal)
    const hasDropList = Boolean(downloadAction && downloadAction.links.length)
    const dropListWithLabel = hasDropList && this.shouldDropListHaveLabel()

    const labelAction = {
      IDLE: strings.DV_WINAPP_ACTION_IDLE,
      WAITING: strings.DV_WINAPP_ACTION_WAITING,
      QUEUED: strings.DV_WINAPP_ACTION_QUEUED,
      DOWNLOADING: strings.DV_WINAPP_ACTION_DOWNLOADING,
      PAUSED: strings.DV_WINAPP_ACTION_PAUSED,
      ERROR: strings.DV_WINAPP_ACTION_ERROR,
      COMPLETED: strings.DV_WINAPP_ACTION_COMPLETED,
      REMOVED: strings.DV_WINAPP_ACTION_REMOVED,
      CANCELLED: strings.DV_WINAPP_ACTION_CANCELLED,
      DISABLED: '',
      CANCELLING: '',
      REMOVING: '',
      RETRYING: '',
      PAUSING: ''
    }

    const labelActionForAtfEpisode = {
      IDLE: fillValues(strings.DV_WINAPP_ACTION_IDLE_EPISODES, { episodeText }),
      WAITING: strings.DV_WINAPP_ACTION_WAITING,
      QUEUED: fillValues(strings.DV_WINAPP_ACTION_QUEUED),
      DOWNLOADING: fillValues(strings.DV_WINAPP_ACTION_DOWNLOADING_EPISODES, { episodeText }),
      PAUSED: fillValues(strings.DV_WINAPP_ACTION_PAUSED_EPISODES, { episodeText }),
      ERROR: fillValues(strings.DV_WINAPP_ACTION_ERROR_EPISODES, { episodeText }),
      COMPLETED: fillValues(strings.DV_WINAPP_ACTION_COMPLETED_EPISODES, { episodeText }),
      REMOVED: fillValues(strings.DV_WINAPP_ACTION_REMOVED_EPISODES, { episodeText }),
      CANCELLED: fillValues(strings.DV_WINAPP_ACTION_CANCELLED_EPISODES, { episodeText }),
      DISABLED: '',
      CANCELLING: '',
      REMOVING: '',
      RETRYING: '',
      PAUSING: ''
    }

    const label = isAtf && isEpisode ? labelActionForAtfEpisode[this.state.downloadStatus] : labelAction[this.state.downloadStatus]

    const { downloadURL } = downloadInfo

    const toggleActionList = (e: React.MouseEvent) => {
      e.preventDefault()
      this.onClick()
    }

    const downloadButtonProps = {
      label,
      downloadURL,
      onClick: hasDropList ? toggleActionList : downloadAction.action,
      status: this.state.downloadStatus,
      progress: this.state.downloadProgress,
    }

    const wrapperClass = cn(st.downloadAction, 'avu-full-width-phablet', {
      [st.downloadActionOpen]: this.state.downloadActionOpen,
      [st.downloadActionInList]: !isAtf,
    })

    return (<>
      <div className={wrapperClass} data-automation-id="download-button">
        {isAtf && <DownloadButtonWithLabel {...downloadButtonProps} forceDoubleHeight={true} atfTreatment={atfTreatment} />}
        {!isAtf && <>
            {hasDropList && dropListWithLabel && (
              <span className={cn(st.dropListLabel, {[st.error]: isErrored})}>
                {label}
              </span>
            )}
            <DownloadButton {...downloadButtonProps} isDropList={hasDropList} />
          </>
        }
        {hasDropList && <DownloadActionLinks downloadId={this.state.downloadId} downloadAction={downloadAction} />}
      </div>
      {this.state.downloadActionOpen && <div className={st.downloadActionOverlay} onClick={this.onClick} />}
      {showDownloadErrorModal && (
        <Modal
          modalId={`modal-error-${downloadInfo.titleID}`}
          ref={this.errorModal}
          footer={''}
          header={strings.DV_WINAPP_DOWNLOAD_ERROR}
        >
          <DownloadStatusMessages messages={downloadStatusMessages} strings={strings} />
        </Modal>
      )}
        {showModalBeforePlay && <BeforePlayModal showModalBeforePlay={showModalBeforePlay} strings={strings} downloadStatus={this.state.downloadStatusPayload} modalRef={this.beforePlayModal} />}
      {showRestrictionModal && (
        <RestrictionModal
          modal={restrictionModalProps}
          closeModal={this.closeRestrictionModal}
          onPinSubmit={this.onPinSubmit}
        />
      )}
      </>
    )
  }
}

type DownloadActionAndLinks = {
  action: (e: React.MouseEvent<Element, MouseEvent>) => void
  links: {
      text: string;
      onClick: (e: React.MouseEvent<Element, MouseEvent>) => void;
      className: string;
  }[]
}
type DownloadActionLinksProps = {downloadAction: DownloadActionAndLinks, downloadId: string}
const DownloadActionLinks = ({downloadAction, downloadId}: DownloadActionLinksProps) => (
  <div className={st.downloadActionsButtons}>
    {downloadAction.links.map((link, i) => (
      <div className={st.downloadActionsButton} key={`${downloadId || ''}-${i}-${link.text || ''}`}>
        <span onClick={link.onClick} className={cn(st.downloadActionButtonTrigger, link.className)}>
          {link.text}
        </span>
      </div>
    ))}
  </div>
)

export const DownloadAction = withRouter(withSpaContext(DownloadActionWithSpaContext))
